import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";

const CHART_COLORS = ["#422AFB", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444"];

/**
 * Myfxbook-style: balance growth % over time + drawdown % (per account).
 */
export default function ClientGrowthDrawdownChart({ accounts, loading }) {
  const [selectedSlot, setSelectedSlot] = useState(null);

  const withPoints = useMemo(
    () => (accounts || []).filter((a) => (a.points || []).length > 0),
    [accounts],
  );

  const active =
    withPoints.find((a) => a.slot === selectedSlot) || withPoints[0] || null;

  useEffect(() => {
    if (withPoints.length && selectedSlot == null) {
      setSelectedSlot(withPoints[0].slot);
    }
  }, [withPoints, selectedSlot]);

  const chart = useMemo(() => {
    if (!active?.points?.length) {
      return { categories: [], growth: [], drawdown: [], growthMax: 1 };
    }
    const categories = active.points.map((p) => p.label);
    const growth = active.points.map((p) => p.growthPercent ?? 0);
    const growthMax = Math.max(1, ...growth.map((g) => Math.abs(Number(g) || 0)));
    return {
      categories,
      growth,
      drawdown: active.points.map((p) => -(p.drawdownPercent ?? 0)),
      growthMax,
    };
  }, [active]);

  const chartSeries = useMemo(
    () => [
      { name: "Growth %", type: "area", data: chart.growth },
      { name: "Drawdown %", type: "line", data: chart.drawdown },
    ],
    [chart.growth, chart.drawdown],
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        height: 320,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: {
        curve: "smooth",
        width: [2, 2],
        dashArray: [0, 4],
      },
      fill: {
        type: ["gradient", "solid"],
        opacity: [0.35, 0],
        gradient: {
          shadeIntensity: 0.3,
          opacityFrom: 0.45,
          opacityTo: 0.05,
        },
      },
      colors: ["#422AFB", "#ef4444"],
      dataLabels: { enabled: false },
      xaxis: {
        categories: chart.categories,
        labels: {
          style: { colors: "#64748b", fontSize: "11px" },
          rotate: -35,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: [
        {
          min: 0,
          max: Math.max(1, chart.growthMax * 1.25),
          tickAmount: 5,
          title: {
            text: "Growth %",
            style: { color: "#64748b", fontSize: "11px" },
          },
          labels: {
            formatter: (v) => `${Number(v).toFixed(1)}%`,
            style: { colors: "#64748b", fontSize: "11px" },
          },
        },
        {
          opposite: true,
          title: {
            text: "Drawdown %",
            style: { color: "#64748b", fontSize: "11px" },
          },
          labels: {
            formatter: (v) => `${Math.abs(Number(v)).toFixed(1)}%`,
            style: { colors: "#64748b", fontSize: "11px" },
          },
        },
      ],
      grid: { borderColor: "#e2e8f0", strokeDashArray: 4 },
      tooltip: {
        shared: true,
        theme: "light",
        y: {
          formatter: (val, { seriesIndex }) =>
            seriesIndex === 1
              ? `${Math.abs(Number(val)).toFixed(2)}% DD`
              : `${Number(val).toFixed(2)}%`,
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        fontSize: "12px",
      },
    }),
    [chart.categories, chart.growthMax],
  );

  if (loading && !withPoints.length) {
    return (
      <div className="flex h-[320px] items-center justify-center text-sm text-gray-500">
        Loading performance chart…
      </div>
    );
  }

  if (!withPoints.length) {
    return (
      <div className="flex h-[320px] flex-col items-center justify-center gap-2 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>Not enough history yet for a growth / drawdown chart.</p>
        <p className="text-xs">
          Refresh this dashboard daily — curves build from balance snapshots only.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {withPoints.length > 1 ? (
        <div className="flex flex-wrap gap-2">
          {withPoints.map((acc, idx) => (
            <button
              key={acc.slot}
              type="button"
              onClick={() => setSelectedSlot(acc.slot)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
                (active?.slot === acc.slot)
                  ? "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-500/15 dark:text-brand-200"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-white/15 dark:bg-navy-900 dark:text-gray-200"
              }`}
            >
              <span
                className="mr-1.5 inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
              />
              {acc.copierDisplayName || `S${acc.slot}`}
            </button>
          ))}
        </div>
      ) : null}

      <div className="h-[320px] min-h-[320px]">
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="line"
          width="100%"
          height="100%"
        />
      </div>

      {active ? (
        <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
          <span>
            Max drawdown:{" "}
            <strong className="text-navy-800 dark:text-white">
              {active.maxDrawdownPercent != null
                ? `${active.maxDrawdownPercent}%`
                : "—"}
            </strong>
          </span>
          {active.sourceNote ? <span>{active.sourceNote}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
